<template>
  <div class="analytics-menu">
    <div
      v-if="!isLoading"
      class="analytics-menu__tabs-wrapper"
      :class="{'analytics-menu__tabs-wrapper_hidden': hideAnalyticsTabs}"
      @click.capture="preventClick"
    >
      <button
        v-if="isScrollableTabs"
        v-show="canScrollLeft"
        class="analytics-menu__scroll-btn left"
        @click="scrollTabs('left')"
      >
        <svg width="20" height="20">
          <use xlink:href="#icon-chevron-left" />
        </svg>
      </button>

      <div
        ref="tabsContainer"
        class="analytics-menu__tabs"
        :class="{ 'is-scrollable': isScrollableTabs }"
      >
        <template v-for="tab in mainTabs">
          <router-link
            v-if="isAnalyticsAvailable(tab.key)"
            :key="tab.key"
            :to="{ name: tab.route }"
            class="analytics-menu__tab"
            :class="{ 'is-scroll-item': isScrollableTabs }"
            active-class="is-active"
            exact-active-class="is-active"
          >
            <div class="analytics-menu__header">
              <div class="analytics-menu__icon">
                <img :src="tab.icon" alt="">
              </div>

              <div class="analytics-menu__text">
                <div class="analytics-menu__title" :title="tab.title">
                  {{ tab.title }}
                </div>
                <div class="analytics-menu__desc" :title="tab.desc">
                  {{ tab.desc }}
                </div>
              </div>
            </div>
          </router-link>

          <div
            v-else
            :key="tab.key + '_disabled'"
            class="analytics-menu__tab is-disabled"
            :class="{ 'is-scroll-item': isScrollableTabs }"
          >
            <div class="analytics-menu__header">
              <div class="analytics-menu__icon">
                <img :src="tab.icon" alt="">
              </div>

              <div class="analytics-menu__text">
                <div class="analytics-menu__title" :title="tab.title">
                  {{ tab.title }}
                </div>
                <div class="analytics-menu__desc" :title="tab.desc">
                  {{ tab.desc }}
                </div>
              </div>

              <svg
                class="analytics-menu__lock"
                width="11"
                height="14"
                role="img"
                aria-label="Услуга не подключена"
              >
                <use xlink:href="#icon-lock" />
              </svg>
            </div>

            <button
              v-if="showButton"
              class="analytics-menu__promo-btn"
              type="button"
              @click.stop="openPromo(tab.key)"
            >
              Подключить
            </button>
          </div>
        </template>
      </div>

      <button
        v-if="isScrollableTabs"
        v-show="canScrollRight"
        class="analytics-menu__scroll-btn right"
        @click="scrollTabs('right')"
      >
        <svg width="20" height="20">
          <use xlink:href="#icon-chevron-right" />
        </svg>
      </button>
    </div>

    <div
      v-if="isLoading"
      class="analytics-menu__loading"
    >
      <SpinnerLoading v-if="isLoading" class="loader_center" color="blue" />
    </div>

    <template v-else-if="actualAnalytics.length === 0">
      <div class="analytics-menu__empty">
        <img src="@/assets/img/icons/message.png" alt="" class="analytics-menu__empty-icon">
        <div class="analytics-menu__empty-subtitle">
          У Вас нет подключенных камер с аналитикой
        </div>
      </div>
      <div class="analytics-menu__empty-spacer" />
    </template>

    <router-view
      v-else
      v-bind="routerViewProps"
    />
  </div>
</template>

<script>
import {
  ROUTE_ANALYTICS,
  ROUTE_PACS_CAR_NUMBER_ANALYTICS,
  ROUTE_PACS_HELMET_ANALYTICS,
  ROUTE_PACS_MASK_ANALYTICS,
  ROUTE_PACS_CROWD_ANALYTICS,
  ROUTE_PACS_PERIMETER_SECURITY_ANALYTICS,
  ROUTE_PACS_AIRCRAFT_ANALYTICS,
  ROUTE_PACS_FALL_DETECTION_ANALYTICS,
  ROUTE_PACS_PEOPLE_COUNT_ANALYTICS,
  ROUTE_PACS_PARKING_DETECTION_ANALYTICS,
  ROUTE_PACS_TRANSPORT_DETECTION_ANALYTICS,
  ROUTE_PACS_CONTAINER_MONITORING_ANALYTICS,
} from "@/router/names.js";

import {ACTION_LOAD_AVAILABLE_ANALYTICS} from "@/store/analytics/index.js";
import PromoAnalyticsDialog from "@/components/pacs/PromoAnalyticsDialog.vue";
import {VENDOR_IS_UFANET} from "@/utils/consts.js";

export default {
  name: "PacsAnalyticsSettingsFrame",
  data() {
    return {
      showButton: false,
      isLoading: true,
      availableAnalytics: [],
      isDragging: false,
      dragged: false,
      startX: 0,
      scrollLeft: 0,
      canScrollLeft: false,
      canScrollRight: false,
      routes: {
        ROUTE_PACS_CAR_NUMBER_ANALYTICS,
        ROUTE_PACS_HELMET_ANALYTICS,
        ROUTE_PACS_MASK_ANALYTICS,
        ROUTE_PACS_CROWD_ANALYTICS,
        ROUTE_PACS_PERIMETER_SECURITY_ANALYTICS,
        ROUTE_PACS_AIRCRAFT_ANALYTICS,
        ROUTE_PACS_FALL_DETECTION_ANALYTICS,
        ROUTE_PACS_PEOPLE_COUNT_ANALYTICS,
        ROUTE_PACS_PARKING_DETECTION_ANALYTICS,
        ROUTE_PACS_TRANSPORT_DETECTION_ANALYTICS,
        ROUTE_PACS_CONTAINER_MONITORING_ANALYTICS
      },
    };
  },
  mounted() {
    this.isLoading = true;
    this.$store.dispatch(`analytics/${ACTION_LOAD_AVAILABLE_ANALYTICS}`)
      .then((analytics) => {
        this.availableAnalytics = analytics;
        this.redirectIfNeeded();
      })
      .finally(() => {
        this.isLoading = false;

        this.$nextTick(() => {
          this.initScrollEvents();
        });
      });

    this.showButton = true;
  },
  beforeDestroy() {
    this.destroyScrollEvents();
  },
  methods: {
    preventClick(e) {
      if (!this.dragged) return;

      const isScrollButton = e.target.closest('.analytics-menu__scroll-btn');
      if (isScrollButton) return;

      e.preventDefault();
      e.stopPropagation();
    },

    initScrollEvents() {
      const container = this.$refs.tabsContainer;
      if (!container) return;

      container.addEventListener("wheel", this.handleTabsWheel, { passive: false });

      container.addEventListener("mousedown", this.onMouseDown);
      window.addEventListener("mouseup", this.onMouseUp);
      window.addEventListener("mousemove", this.onMouseMove);
      container.addEventListener("scroll", this.checkScrollIndicators);

      this.checkScrollIndicators();
      window.addEventListener("resize", this.checkScrollIndicators);
    },

    destroyScrollEvents() {
      const container = this.$refs.tabsContainer;
      if (!container) return;

      container.removeEventListener("wheel", this.handleTabsWheel);
      container.removeEventListener("mousedown", this.onMouseDown);
      window.removeEventListener("mouseup", this.onMouseUp);
      window.removeEventListener("mousemove", this.onMouseMove);
      container.removeEventListener("scroll", this.checkScrollIndicators);
      window.removeEventListener("resize", this.checkScrollIndicators);
    },

    onMouseDown(e) {
      if (
        e.target.closest('.analytics-menu__scroll-btn') ||
        e.target.closest('.analytics-menu__promo-btn')
      ) {
        return;
      }

      this.isDragging = true;
      this.dragged = false;

      const container = this.$refs.tabsContainer;
      container.style.cursor = "grabbing";
      container.style.userSelect = "none";
      this.startX = e.clientX;
      this.scrollLeft = container.scrollLeft;

      e.preventDefault();
    },

    onMouseUp() {
      if (!this.isDragging) return;

      this.isDragging = false;

      const container = this.$refs.tabsContainer;
      if (container) {
        container.style.cursor = "";
        container.style.userSelect = "";
      }

      setTimeout(() => {
        this.dragged = false;
      }, 0);
    },

    onMouseMove(e) {
      if (!this.isDragging) return;
      e.preventDefault();

      const container = this.$refs.tabsContainer;
      if (!container) return;

      const dx = e.clientX - this.startX;
      const walk = dx * 1.5;

      if (Math.abs(walk) > 3) {
        this.dragged = true;
      }

      container.scrollLeft = this.scrollLeft - walk;
    },

    scrollTabs(direction) {
      const container = this.$refs.tabsContainer;
      if (!container) return;

      const scrollAmount = 600;

      container.scrollBy({
        left: direction === "left" ? -scrollAmount : scrollAmount,
        behavior: "smooth"
      });
    },

    checkScrollIndicators() {
      const container = this.$refs.tabsContainer;
      if (!container || !this.isScrollableTabs) {
        this.canScrollLeft = false;
        this.canScrollRight = false;
        return;
      }

      // Добавляем небольшой допуск для более точного определения
      const tolerance = 2;
      this.canScrollLeft = container.scrollLeft > tolerance;
      this.canScrollRight = container.scrollLeft + container.clientWidth < container.scrollWidth - tolerance;
    },

    handleTabsWheel(e) {
      const container = this.$refs.tabsContainer;
      if (!container || !this.isScrollableTabs) return;

      const hasHorizontalOverflow = container.scrollWidth > container.clientWidth;
      if (!hasHorizontalOverflow) return;

      e.preventDefault();
      container.scrollLeft += e.deltaY;
    },

    redirectIfNeeded() {
      if (this.$route.name !== ROUTE_ANALYTICS) return;

      const firstAvailableTab = this.mainTabs.find(tab =>
        this.isAnalyticsAvailable(tab.key)
      );

      if (firstAvailableTab) {
        this.$router.replace({ name: firstAvailableTab.route });
      }
    },

    isAnalyticsAvailable(analyticsKey) {
      return (this.availableAnalytics || []).includes(analyticsKey);
    },

    openPromo(analyticsKey) {
      this.$camsdals.open(
        PromoAnalyticsDialog,
        { analyticsKey },
        { dialogTitle: "Подключение услуг" },
        { size: "vuedal-auto-width" }
      );
    }
  },
  computed: {
    hideAnalyticsTabs() {
      return this.$route.matched.some(routeRecord => routeRecord.meta && routeRecord.meta.hideAnalyticsTabs);
    },
    isScrollableTabs() {
      return this.mainTabs.length > 3;
    },
    actualAnalytics() {
      const supportedKeys = [
        'people_counter',
        'perimeter_security',
        'aircraft',
        'fall_detection',
        'crowd',
        'parking_detection',
        'transport_detection',
        'container_monitoring'
      ];

      return (this.availableAnalytics || []).filter(key =>
        supportedKeys.includes(key)
      );
    },
    routerViewProps() {
      if (this.$route.name !== ROUTE_PACS_PARKING_DETECTION_ANALYTICS) {
        return {};
      }

      return {
        availableAnalytics: this.availableAnalytics,
      };
    },
    mainTabs() {
      const promoTabsKeys = ['people_counter', 'perimeter_security', 'crowd'];
      const optionalTabsKeys = ['fall_detection', 'parking_detection', 'transport_detection', 'container_monitoring', 'aircraft'];

      const tabs = [
        {
          key: 'people_counter',
          route: this.routes.ROUTE_PACS_PEOPLE_COUNT_ANALYTICS,
          icon: require('@/assets/img/people_counter.png'),
          title: 'Подсчет посетителей',
          desc: 'Узнайте сколько человек к вам пришло',
        },
        {
          key: 'perimeter_security',
          route: this.routes.ROUTE_PACS_PERIMETER_SECURITY_ANALYTICS,
          icon: require('@/assets/img/perimeter_security.png'),
          title: 'Вторжение в зону',
          desc: 'Мы оповестим о появлении людей в заданной области',
        },
        {
          key: 'aircraft',
          route: this.routes.ROUTE_PACS_AIRCRAFT_ANALYTICS,
          icon: require('@/assets/img/perimeter_security.png'),
          title: this.$t('aircraftDetector'),
          desc: 'Детекция летательных аппаратов',
        },
        {
          key: 'fall_detection',
          route: this.routes.ROUTE_PACS_FALL_DETECTION_ANALYTICS,
          icon: require('@/assets/img/fall_detection.png'),
          title: 'Обнаружение падений',
          desc: 'Мы оповестим о падении человека в заданной области',
        },
        {
          key: 'crowd',
          route: this.routes.ROUTE_PACS_CROWD_ANALYTICS,
          icon: require('@/assets/img/crowd.png'),
          title: 'Детекция очереди',
          desc: 'Следите за очередью и оповещайте сотрудников',
        },
        {
          key: 'parking_detection',
          route: this.routes.ROUTE_PACS_PARKING_DETECTION_ANALYTICS,
          icon: require('@/assets/img/parking_detection.png'),
          title: 'Умные парковки',
          desc: 'Следите за наличием свободных мест на парковке',
        },
        {
          key: 'transport_detection',
          route: this.routes.ROUTE_PACS_TRANSPORT_DETECTION_ANALYTICS,
          icon: require('@/assets/img/transport_detection.png'),
          title: this.$t('transportDetection') || 'Транспорт в зоне',
          desc: 'Следите за появлением авто в выбранной зоне',
        },
        {
          key: 'container_monitoring',
          route: this.routes.ROUTE_PACS_CONTAINER_MONITORING_ANALYTICS,
          icon: require('@/assets/img/container-monitoring.png'),
          title: this.$t('containerMonitoring') || 'Контроль ТКО',
          desc: 'Следите за состоянием площадок ТКО',
        }
      ].filter(tab => {
        if (promoTabsKeys.includes(tab.key)) return true;
        if (optionalTabsKeys.includes(tab.key)) return this.isAnalyticsAvailable(tab.key);
        return false;
      });

      const availableTabs = tabs.filter(tab => this.isAnalyticsAvailable(tab.key));
      const unavailableTabs = tabs.filter(tab => !this.isAnalyticsAvailable(tab.key));

      return [...availableTabs, ...unavailableTabs];
    }
  },
  watch: {
    hideAnalyticsTabs(isHidden) {
      if (!isHidden) this.$nextTick(this.checkScrollIndicators);
    },
  },
};
</script>

<style lang="scss">
.analytics-menu {
  display: flex;
  flex-direction: column;
  height: 100%;
  position: relative;

  &__tabs-wrapper {
    position: relative;
    display: flex;
    align-items: center;
    gap: 12px;
    padding-bottom: 10px;
    flex-shrink: 0;

    &_hidden {
      display: none;
    }
  }

  &__scroll-btn {
    position: absolute;
    top: calc(50% - 5px);
    transform: translateY(-50%);
    z-index: 10;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: white;
    border: 1px solid #e5e7eb;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: #4b5563;
    transition: all 0.2s ease;

    &:hover {
      background: #f9fafb;
      color: #111827;
      box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
    }

    &.left {
      left: -18px;
    }

    &.right {
      right: -18px;
    }
  }

  &__tabs {
    display: flex;
    flex-wrap: wrap;
    gap: 24px;
    flex-shrink: 0;
    width: 100%;
  }

  &__tabs.is-scrollable {
    flex-wrap: nowrap;
    overflow-x: auto;
    overflow-y: hidden;
    scrollbar-width: none;
    -webkit-user-select: none;
    user-select: none;
    cursor: grab;

    &:active {
      cursor: grabbing;
    }
  }

  &__tabs.is-scrollable::-webkit-scrollbar {
    display: none;
  }

  &__tab {
    position: relative;
    flex: 1 1 266px;
    min-width: 0;
    max-width: 100%;

    display: flex;
    flex-direction: column;
    gap: 12px;

    padding: 12px;
    border-radius: 8px;

    border: 1px solid #edf1f7;
    background: #fff;

    text-decoration: none;
    color: inherit;

    transition: border-color 0.15s ease;
  }

  &__tab:hover,
  &__tab:focus-within,
  &__tab.is-active {
    border-color: #2f84f8;
  }

  &__tab.is-scroll-item {
    flex: 0 0 max(266px, calc(70% / 3));
  }

  &__tab.is-disabled {
    cursor: default;
    overflow: hidden;
  }

  &__header {
    display: flex;
    align-items: center;
    gap: 12px;
    min-height: 50px;
  }

  &__icon {
    flex: 0 0 40px;
    width: 40px;
    height: 40px;

    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__text {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
  }

  &__lock {
    flex: 0 0 11px;
    align-self: flex-start;
    margin-top: 1px;
  }

  &__title {
    font-size: 12px;
    line-height: 16px;
    font-weight: 700;
    color: #191919;

    margin: 0 0 4px;

    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
  }

  &__desc {
    font-size: 12px;
    line-height: 16px;
    color: #555;

    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;

    white-space: normal;
  }
}

.analytics-menu__icon svg,
.analytics-menu__icon img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  -webkit-user-drag: none;
}

.analytics-menu .analytics-menu__promo-btn {
  width: 100%;
  min-height: 32px;
  margin-top: auto;
  padding: 8px 12px;
  border-radius: 24px;
  background: #2f84f8;
  color: #fff;
  border: none;
  font-size: 12px;
  line-height: 16px;
  font-weight: 400;
  cursor: pointer;
  transition: background-color 0.15s ease;

  &:hover {
    background: #2475e4;
  }

  &:focus-visible {
    outline: 2px solid #2f84f8;
    outline-offset: 2px;
  }
}

.analytics-menu__empty {
  display: flex;
  flex: 8 1 0;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  background-color: #EDF1F7;
  border-radius: 24px;
  opacity: 1;
}

.analytics-menu__empty-icon {
  width: 48px;
  height: 48px;
  margin-bottom: 24px;
}

.analytics-menu__empty-title {
  font-size: 16px;
  font-weight: 700;
  color: #111;
  margin-bottom: 8px;
}

.analytics-menu__empty-subtitle {
  font-size: 14px;
  color: #6b7280;
}

.analytics-menu__empty-spacer {
  flex: 1 1 0;
}
</style>
